import { useEffect, useRef } from 'react'
import { SET_MATCH_TOLERANCE } from '../utils/annotationSets'

// Arret automatique sur les frames annotees — ANNOTATIONS-002.
//
// UNE SEULE IMPLEMENTATION POUR DEUX SURFACES · le mode lecture du Studio et la
// page de seance publique. La regle est la meme — « la video s'arrete d'elle-
// meme sur l'annotation, le coach commente, il relance a la main » — et deux
// copies auraient divergé. On l'a vu trois fois dans la journee sur ce module ·
// le `ry` du halo en dur a trois endroits, les bornes du rayon ecrites dans le
// seul gestionnaire de redimensionnement, et les dix sites qui lisaient le
// centre d'un halo.
//
// DETECTION PAR requestAnimationFrame, ET NON PAR `timeupdate`. Celui-ci ne
// tombe qu'environ 4 fois par seconde, soit jusqu'a 250 ms de retard — 6 images
// a 25 im/s. La video s'arreterait APRES l'action a montrer. La boucle lit
// `video.currentTime` directement, sans aucun setState par frame ; le seul
// rendu declenche est celui de l'arret.

// Au-dela de cet ecart, la tete de lecture n'est plus sur la frame figee · le
// coach a relance ou scrube. On leve l'arret, sans quoi le dessin resterait
// pose sur une image en mouvement et la detection serait bloquee pour de bon.
const DERIVE_MAX = 0.25

// Un bond de plus d'une demi-seconde entre deux frames est un deplacement
// discret, pas une tete de lecture qui avance.
const SAUT_MAX = 0.5

/**
 * @param playerRef  ref imperative exposant getCurrentTime()
 * @param options
 *   active       la boucle tourne
 *   points       [{t, ...}] tries par t · les frames ou s'arreter
 *   segments     [{start, end}] tries, ou null · lecture continue sans decoupe
 *   heldPoint    le point actuellement fige, ou null · pilote par l'appelant
 *   onStop       (point) => void · une frame annotee vient d'etre franchie
 *   onRelease    () => void      · l'arret n'a plus lieu d'etre
 *   onSegmentEnd (index) => void · la fin d'un segment vient d'etre franchie
 *   onFinished   () => void      · passe le dernier point (sans segments)
 */
export function useAnnotationStops(playerRef, {
  active,
  points,
  segments = null,
  heldPoint = null,
  onStop,
  onRelease,
  onSegmentEnd,
  onFinished,
}) {
  // Tout passe par des refs · la boucle ne doit se reabonner qu'au changement
  // de `active`. Se reabonner a chaque rendu la ferait repartir de zero et
  // perdre `lastTime`, donc rater des franchissements.
  const pointsRef = useRef(points)
  const segmentsRef = useRef(segments)
  const heldRef = useRef(heldPoint)
  const cbRef = useRef({})
  const lastTimeRef = useRef(0)
  const scrubbingRef = useRef(false)
  const finishedRef = useRef(false)

  useEffect(() => { pointsRef.current = points }, [points])
  useEffect(() => { segmentsRef.current = segments }, [segments])
  useEffect(() => { heldRef.current = heldPoint }, [heldPoint])
  useEffect(() => {
    cbRef.current = { onStop, onRelease, onSegmentEnd, onFinished }
  }, [onStop, onRelease, onSegmentEnd, onFinished])

  useEffect(() => {
    if (!active) return
    let raf = 0
    finishedRef.current = false
    lastTimeRef.current = playerRef.current?.getCurrentTime() ?? 0

    const tick = () => {
      raf = requestAnimationFrame(tick)
      const maintenant = playerRef.current?.getCurrentTime() ?? 0

      // Un arret est en cours. Les controles du player restent actifs · si le
      // coach relance lui-meme, ou scrube ailleurs, on leve l'arret.
      const fige = heldRef.current
      if (fige) {
        lastTimeRef.current = maintenant
        if (Math.abs(maintenant - fige.t) > DERIVE_MAX) cbRef.current.onRelease?.()
        return
      }

      const avant = lastTimeRef.current
      lastTimeRef.current = maintenant

      // Retour en arriere (scrub manuel) · on ne declenche rien. Aucun point
      // n'est « consomme », donc reculer rejoue bien l'arret.
      if (maintenant < avant) return

      // Le declenchement se fait en lecture, pas en manipulation · figer
      // l'image sous les doigts du coach qui drague serait absurde.
      if (scrubbingRef.current || maintenant - avant > SAUT_MAX) return

      const liste = pointsRef.current || []
      const franchi = liste.find(p => p.t > avant && p.t <= maintenant)
      if (franchi) { cbRef.current.onStop?.(franchi); return }

      // Fin de segment · on la teste APRES les points, pour qu'une annotation
      // posee sur la derniere image d'un segment provoque bien son arret avant
      // qu'on saute ailleurs.
      const seg = segmentsRef.current
      if (seg) {
        const i = seg.findIndex(s => s.end > avant && s.end <= maintenant)
        if (i >= 0) { cbRef.current.onSegmentEnd?.(i); return }
        return
      }

      // Sans segments · passe le dernier point, le mode se termine tout seul.
      // Teste ici et non dans un effet, parce que la boucle est la seule chose
      // qui tourne en continu — un effet ne se rejouerait pas.
      const dernier = liste[liste.length - 1]
      if (dernier && maintenant > dernier.t + 0.5 && !finishedRef.current) {
        finishedRef.current = true
        cbRef.current.onFinished?.()
      }
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [active, playerRef])

  // Suivi du geste de scrub. En capture, pour voir le pointerdown avant que la
  // barre de progression ou la timeline ne l'aient traite.
  useEffect(() => {
    if (!active) return
    const onDown = () => { scrubbingRef.current = true }
    const onUp = () => {
      scrubbingRef.current = false
      // Relacher sur une frame annotee · l'arret se produit, comme si on
      // l'avait franchie en lecture. Sans ca, viser une annotation a la main
      // ne donnerait rien tant qu'on ne repasse pas dessus.
      if (heldRef.current) return
      const t = playerRef.current?.getCurrentTime() ?? 0
      // Meme question metier que la reouverture d'une annotation · « cette
      // frame est-elle la frame annotee ? ». Un relachement de scrub EST un
      // retour manuel, le geste pour lequel un seuil serre ne convient pas.
      const proche = (pointsRef.current || []).find(
        p => Math.abs(p.t - t) <= SET_MATCH_TOLERANCE)
      if (proche) cbRef.current.onStop?.(proche)
    }
    window.addEventListener('pointerdown', onDown, true)
    window.addEventListener('pointerup', onUp, true)
    window.addEventListener('pointercancel', onUp, true)
    return () => {
      window.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('pointerup', onUp, true)
      window.removeEventListener('pointercancel', onUp, true)
      scrubbingRef.current = false
    }
  }, [active, playerRef])
}

export default useAnnotationStops
